import { format } from 'date-fns'
import { nl, enUS } from 'date-fns/locale'
import { 
  FileText, 
  Zap, 
  Target, 
  Brain, 
  Trophy, 
  BarChart3, 
  Palette, 
  Send, 
  RefreshCw,
  CheckCircle,
  Clock,
  TrendingUp,
  Shield,
  Sparkles,
  Users,
  CalendarIcon
} from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, data, Form } from 'react-router'
import { LanguageSwitcher } from '#app/components/language-switcher-simple.tsx'
import { MarketingFooter } from '#app/components/marketing-footer.tsx'
import { MarketingHeader } from '#app/components/marketing-header.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Calendar } from '#app/components/ui/calendar.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Input } from '#app/components/ui/input.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '#app/components/ui/popover.tsx'
import { getUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'
import { type Route } from './+types/_index.ts'
import { CompanySearchResults } from './_marketing+/company-search-simple.tsx'

export async function loader({ request }: Route.LoaderArgs) {
  const userId = await getUserId(request)
  const user = userId
    ? await prisma.user.findUnique({
        where: { id: userId },
        select: { id: true, name: true, username: true },
      })
    : null

  const now = new Date()
  const hour = now.getHours()
  let greetingKey = 'night'
  if (hour >= 6 && hour < 12) greetingKey = 'morning'
  else if (hour >= 12 && hour < 18) greetingKey = 'afternoon'
  else if (hour >= 18 && hour < 23) greetingKey = 'evening'

  return data({ user, greetingKey })
}

export default function Index({ loaderData }: Route.ComponentProps) {
  const { user, greetingKey } = loaderData
  const optionalUser = useOptionalUser()
  
  // If user is logged in, show the create quote interface
  if (user) {
    return <CreateQuoteInterface user={user} greetingKey={greetingKey} />
  }
  
  // Otherwise show the marketing landing page
  return <MarketingLandingPage />
}

function CreateQuoteInterface({ user, greetingKey }: { user: any; greetingKey: string }) {
  const { t, i18n } = useTranslation(['app', 'common'])
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [companyName, setCompanyName] = useState('')
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState<{ 
    name: string; 
    image?: string;
    description?: string;
    founded?: string;
    employees?: string;
    website?: string;
  } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  
  // Get the appropriate date-fns locale based on current language
  const getDateLocale = () => {
    const localeMap: Record<string, any> = {
      'en': enUS,
      'nl': nl,
    }
    return localeMap[i18n.language] || enUS
  }

  const handleInputFocus = () => {
    setShowDatePicker(true)
  }

  // Handle click outside to collapse
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (companyName.length === 0) {
          setShowDatePicker(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [companyName])

  const greeting = t(`app:greeting.${greetingKey}`)
  const displayGreeting = `${greeting}, ${user.name || user.username}`

  const dateDisplay =
    selectedDate.toDateString() === new Date().toDateString()
      ? t('common:time.today')
      : format(selectedDate, 'd MMMM yyyy', { locale: getDateLocale() })

  return (
    <div className="flex flex-col min-h-screen">
      <main className="flex-1 font-poppins grid place-items-center">
        <div className="w-full max-w-2xl px-4 py-16">
        <h1 className="text-4xl font-medium font-[family-name:var(--font-heading)] text-center mb-8">
          {displayGreeting}
        </h1>

        <Form method="get" action="/quotes/wizard" className="space-y-4">
          <div ref={containerRef} className="relative">
            <div className={cn(
              "relative border rounded-lg transition-all duration-300 bg-background",
              showDatePicker ? "border-primary" : "border-input"
            )}>
              <Input
                name="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={() => {
                  if (companyName.length === 0) {
                    setTimeout(() => {
                      setShowDatePicker(false)
                    }, 100)
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && companyName.trim()) {
                    e.preventDefault()
                    const form = e.currentTarget.closest('form')
                    if (form) {
                      form.requestSubmit()
                    }
                  }
                }}
                placeholder={t('app:quotes.form.companyPlaceholder')}
                className={cn(
                  "w-full px-4 py-6 bg-transparent outline-none text-lg border-0 focus:ring-0",
                  "placeholder:text-muted-foreground",
                  "pr-32 transition-all duration-300"
                )}
              />
              
              <Button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2"
                disabled={!companyName}
              >
                {t('app:quotes.createQuote')} →
              </Button>
            </div>
            
            {/* Date picker section underneath the input */}
            <div className={cn(
              "mt-3 transition-all duration-300",
              showDatePicker ? "opacity-100" : "opacity-0 pointer-events-none"
            )}>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{t('app:quotes.quoteDate')}:</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        'h-8 px-2 justify-start text-left font-normal',
                        !selectedDate && 'text-muted-foreground',
                      )}
                    >
                      <CalendarIcon className="mr-2 h-3 w-3" />
                      {dateDisplay}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => date && setSelectedDate(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          <input type="hidden" name="quoteDate" value={selectedDate.toISOString()} />
          {selectedCompany && (
            <>
              {selectedCompany.image && <input type="hidden" name="companyImage" value={selectedCompany.image} />}
              {selectedCompany.description && <input type="hidden" name="companyDescription" value={selectedCompany.description} />}
              {selectedCompany.founded && <input type="hidden" name="companyFounded" value={selectedCompany.founded} />}
              {selectedCompany.employees && <input type="hidden" name="companyEmployees" value={selectedCompany.employees} />}
              {selectedCompany.website && <input type="hidden" name="companyWebsite" value={selectedCompany.website} />}
            </>
          )}
        </Form>

        {/* Company search results */}
        {companyName && (
          <CompanySearchResults 
            companyName={companyName} 
            selectedDate={selectedDate}
            onCompanySelect={setSelectedCompany}
          />
        )}
        </div>
      </main>
    </div>
  )
}

function MarketingLandingPage() {
  const { t } = useTranslation(['marketing', 'common'])
  
  return (
    <div className="flex flex-col min-h-screen">
      <MarketingHeader />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32">
          <div className="container">
            <div className="grid gap-12 lg:grid-cols-2 lg:gap-8 items-center">
              <div className="space-y-8">
                <div className="space-y-4">
                  <h1 className="text-4xl font-bold font-[family-name:var(--font-heading)] tracking-tighter sm:text-5xl xl:text-6xl/none">
                    {t('marketing:hero.title')}
                  </h1>
                  <p className="text-xl text-muted-foreground">
                    {t('marketing:hero.subtitle')}
                  </p>
                </div>
                <Link to="/signup">
                  <Button size="lg" className="text-lg px-8">
                    {t('marketing:hero.cta')}
                  </Button>
                </Link>
              </div>
              <div className="relative">
                <div className="aspect-video rounded-lg bg-muted flex items-center justify-center">
                  <FileText className="w-24 h-24 text-muted-foreground" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pain Point Section */}
        <section className="py-16 bg-muted/50">
          <div className="container max-w-3xl text-center space-y-4">
            <h2 className="text-3xl font-bold font-[family-name:var(--font-heading)]">
              {t('marketing:painPoint.title')}
            </h2>
            <p className="text-lg text-muted-foreground">
              {t('marketing:painPoint.subtitle')}
            </p>
          </div>
        </section>

        {/* How it Works Section */}
        <section className="py-20">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold font-[family-name:var(--font-heading)] mb-4">{t('marketing:howItWorks.title')}</h2>
              <p className="text-lg text-muted-foreground">{t('marketing:howItWorks.subtitle')}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Target className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold font-[family-name:var(--font-heading)]">{t('marketing:howItWorks.steps.0.title')}</h3>
                <p className="text-muted-foreground">
                  {t('marketing:howItWorks.steps.0.description')}
                </p>
              </div>
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Sparkles className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold font-[family-name:var(--font-heading)]">{t('marketing:howItWorks.steps.1.title')}</h3>
                <p className="text-muted-foreground">
                  {t('marketing:howItWorks.steps.1.description')}
                </p>
              </div>
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Send className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold font-[family-name:var(--font-heading)]">{t('marketing:howItWorks.steps.2.title')}</h3>
                <p className="text-muted-foreground">
                  {t('marketing:howItWorks.steps.2.description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="functies" className="py-20 bg-muted/50">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold font-[family-name:var(--font-heading)] mb-4">{t('marketing:features.title')}</h2>
              <p className="text-lg text-muted-foreground">{t('marketing:features.subtitle')}</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-background rounded-lg p-6 space-y-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Brain className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold font-[family-name:var(--font-heading)]">{t('marketing:features.list.0.title')}</h3>
                <p className="text-muted-foreground">
                  {t('marketing:features.list.0.description')}
                </p>
              </div>
              <div className="bg-background rounded-lg p-6 space-y-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold font-[family-name:var(--font-heading)]">{t('marketing:features.list.1.title')}</h3>
                <p className="text-muted-foreground">
                  {t('marketing:features.list.1.description')}
                </p>
              </div>
              <div className="bg-background rounded-lg p-6 space-y-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold font-[family-name:var(--font-heading)]">{t('marketing:features.list.2.title')}</h3>
                <p className="text-muted-foreground">
                  {t('marketing:features.list.2.description')}
                </p>
              </div>
              <div className="bg-background rounded-lg p-6 space-y-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Palette className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold font-[family-name:var(--font-heading)]">{t('marketing:features.list.3.title')}</h3>
                <p className="text-muted-foreground">
                  {t('marketing:features.list.3.description')}
                </p>
              </div>
              <div className="bg-background rounded-lg p-6 space-y-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold font-[family-name:var(--font-heading)]">{t('marketing:features.list.4.title')}</h3>
                <p className="text-muted-foreground">
                  {t('marketing:features.list.4.description')}
                </p>
              </div>
              <div className="bg-background rounded-lg p-6 space-y-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <RefreshCw className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold font-[family-name:var(--font-heading)]">{t('marketing:features.list.5.title')}</h3>
                <p className="text-muted-foreground">
                  {t('marketing:features.list.5.description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-20">
          <div className="container max-w-3xl">
            <div className="bg-muted rounded-lg p-8 text-center space-y-4">
              <p className="text-xl italic font-[family-name:var(--font-heading)]">
                "{t('marketing:testimonial.quote')}"
              </p>
              <div className="text-sm">
                <p className="font-semibold">{t('marketing:testimonial.author')}</p>
                <p className="text-muted-foreground">{t('marketing:testimonial.role')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 bg-primary text-primary-foreground">
          <div className="container text-center space-y-8">
            <h2 className="text-4xl font-bold font-[family-name:var(--font-heading)]">
              {t('marketing:finalCta.title')}
            </h2>
            <Link to="/signup">
              <Button size="lg" variant="secondary" className="text-lg px-8">
                {t('marketing:finalCta.button')}
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  )
}